import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import API from "services/axios";
import authHeader from "services/auth-header";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import { useNavigate } from "react-router-dom";

export default function AddEfsInvoice({ showModal }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ******************** All States ************************* */
  const [formData, setFormData] = useState({});
  const [checkingInvoice, setCheckingInvoice] = useState(false);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [invoiceError, setInvoiceError] = useState("");

  /* ******************** All Functions ************************* */

  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.post("/manufacturer/efs-invoice", formData, {
        headers: authHeader(),
      });
      if (data?.success) {
        // Handle success scenario
        navigate(`/manufacturer/efs-invoice/details/${data?.invId}`);
      } else {
        throw new Error(data?.message || "Failed to add project");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  /* ******************** All UseEffects ************************* */
  useEffect(() => {
    const invoiceNo = formData?.invoiceNo?.trim();

    if (!invoiceNo) {
      setExistingInvoices([]);
      setInvoiceError("");
      return;
    }

    let isCancelled = false;
    setCheckingInvoice(true);

    const timeoutId = setTimeout(async () => {
      try {
        const { data } = await API.post(
          "/manufacturer/efs-invoices?page=1&limit=5",
          { query: invoiceNo },
          { headers: authHeader() }
        );

        if (isCancelled) return;

        if (data?.success) {
          const list = data?.data || [];
          setExistingInvoices(list);

          const hasExactMatch = list.some(
            (inv) =>
              String(inv.invoiceNo || "")
                .trim()
                .toLowerCase() === invoiceNo.toLowerCase()
          );

          setInvoiceError(
            hasExactMatch ? "An invoice with this number already exists." : ""
          );
        } else {
          setExistingInvoices([]);
          setInvoiceError("");
        }
      } catch (err) {
        if (!isCancelled) {
          setExistingInvoices([]);
          setInvoiceError("");
        }
      } finally {
        if (!isCancelled) {
          setCheckingInvoice(false);
        }
      }
    }, 400);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [formData?.invoiceNo]);

  return (
    <div>
      <Modal.Header className="py-0" closeButton>
        <Modal.Title>Add EFS Invoice</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Invoice Number</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Enter Invoice No#"
                value={formData?.invoiceNo || ""}
                onChange={(e) =>
                  setFormData({ ...formData, invoiceNo: e.target.value })
                }
              />
              {checkingInvoice && (
                <div className="small text-muted mt-1">Checking...</div>
              )}
              {invoiceError && (
                <div className="small text-danger mt-1">{invoiceError}</div>
              )}
              {!invoiceError && existingInvoices.length > 0 && (
                <div className="mt-2">
                  <div className="small text-muted mb-1">
                    Existing invoices with similar numbers:
                  </div>
                  <ul className="small mb-0">
                    {existingInvoices.map((inv) => (
                      <li key={inv.id || inv.invId}>
                        <strong>{inv.invoiceNo}</strong>
                        {inv.invoiceDate ? ` - ${inv.invoiceDate}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="pb-0 pt-2">
        <button
          onClick={(e) => {
            showModal(false);
          }}
          className="btn btn-sm btn-secondary"
        >
          Cancel
        </button>
        <button
          className="btn btn-sm btn-primary"
          disabled={
            checkingInvoice ||
            !!invoiceError ||
            !formData?.invoiceNo ||
            !formData.invoiceNo.trim()
          }
        >
          Add
        </button>
      </Modal.Footer>
    </div>
  );
}
